import {
  type DiscountDTO,
  type OldProductDTO,
  type ProductImages,
  type ProductLightDTO,
  type ProductMedia,
  ProductMediaType,
  type ProductPrice
} from 'ecosystem'
import { httpImageFactory } from './urls'
import { parseFloatSafe } from './numbers'
import { isNullable } from './general'

// When `excludeVat` is true and the BE returned a non-null net value, use the
// net field. Otherwise fall back to the gross field — never silently render
// `null`/`undefined` as 0.
export const getProductOriginalPrice = (product: ProductPrice, excludeVat = false): number => {
  if (excludeVat && !isNullable(product.originalPriceWithoutVat)) {
    return product.originalPriceWithoutVat ?? 0
  }
  return product.originalPrice ?? 0
}

// A product is price-on-request when the merchant flagged it AND the shopper has
// no negotiated price for it. A subscriber whose price-list row matched buys it at
// that price instead. `subscriberPrice` is only ever true for a logged-in BUSINESS
// shopper with an active subscriber, so no customer-type check belongs here.
export const isPriceOnRequest = (product: {
  priceRequest?: boolean
  subscriberPrice?: boolean
}): boolean => !!product.priceRequest && !product.subscriberPrice

export const getProductPrice = (product: ProductPrice, excludeVat = false): number => {
  if (excludeVat && !isNullable(product.priceWithoutVat)) {
    return product.priceWithoutVat ?? getProductOriginalPrice(product, excludeVat)
  }
  return product.price ?? getProductOriginalPrice(product, excludeVat)
}

const isDiscountApplicableToProduct = (product: ProductLightDTO, discountCode: DiscountDTO) => {
  return (
    (product.brand?.id && discountCode.brandIds?.includes(product.brand.id)) ||
    discountCode.categoryIds?.some((c) => product.categoryIds?.includes(c)) ||
    discountCode.tagIds?.some((t) => product.tags?.map((pTag) => pTag.id).includes(t))
  )
}

export const getProductDiscountPrice = (
  product: ProductLightDTO,
  discountCode: DiscountDTO | null
):
  | {
      discountedPrice: number
      priceUsedForDiscount: number
      /** Net analog of `discountedPrice`. Same selection rule (campaign vs.
       *  original-vs-current) applied to the net fields, so callers that drive
       *  ex-VAT totals (cart summary in BUSINESS / VAT-toggle mode, B2B invoice
       *  flow) see the discount reflected. */
      discountedPriceWithoutVat: number
      priceUsedForDiscountWithoutVat: number
    }
  | undefined => {
  if (!discountCode || !isDiscountApplicableToProduct(product, discountCode)) {
    return undefined
  }

  const discountRate = discountCode.value / 100
  const itemPrice = getProductPrice(product)
  const itemOriginalPrice = getProductOriginalPrice(product)
  const itemPriceNet = getProductPrice(product, true)
  const itemOriginalPriceNet = getProductOriginalPrice(product, true)
  const isOriginalPriceLower = itemPrice > itemOriginalPrice

  if (!discountCode.campaign && !isOriginalPriceLower) {
    return {
      priceUsedForDiscount: itemOriginalPrice,
      priceUsedForDiscountWithoutVat: itemOriginalPriceNet,
      discountedPrice: itemOriginalPrice * (1 - discountRate),
      discountedPriceWithoutVat: itemOriginalPriceNet * (1 - discountRate)
    }
  }

  return {
    priceUsedForDiscount: itemPrice,
    priceUsedForDiscountWithoutVat: itemPriceNet,
    discountedPrice: itemPrice * (1 - discountRate),
    discountedPriceWithoutVat: itemPriceNet * (1 - discountRate)
  }
}

export const getProductWeight = (fullProduct: OldProductDTO) => {
  const weightSpec = fullProduct.specifications?.find((spec) => {
    return /^(?:weight|vikt|vekt|vægt|paino|gewicht)/.test(spec.name.toLowerCase())
  })

  let weight = 0

  if (weightSpec) {
    const unit = /\((kg|g)\)/.exec(weightSpec.name.toLowerCase())?.[1] ?? 'kg'
    weight = parseFloatSafe(weightSpec.value) ?? 0

    if (unit === 'g') {
      weight = weight / 1000
    }
  }

  return weight
}

// TEMPORARY — nordik.se data migration.
// `main` is only ever meant to mark a product's primary image, but nordik.se's
// imported medias also set it on DOCUMENT entries pointing at product pages.
// Those are a data error rather than a real attachment, so keep them out of the
// attached-files tab. Once the nordik.se medias are corrected in the DB, delete
// this helper and go back to a plain `media.type === ProductMediaType.DOCUMENT`
// check at each call site.
export const isAttachedDocument = (media: ProductMedia): boolean =>
  media.type === ProductMediaType.DOCUMENT && !media.main

// A product can flag a non-image media (a DOCUMENT link, a VIDEO) as `main`, so
// the `main` flag alone is not enough — filter on the type too, otherwise the
// document URL is handed to `next/image` and the product renders with no image
// even though it has a perfectly good one further down the list.
export const getProductImages = (medias: ProductMedia[] | null): ProductImages => {
  const mainImages = medias?.filter(
    (mediaItem) => mediaItem.main && mediaItem.type === ProductMediaType.IMAGE
  )
  const secondaryImages = medias?.filter(
    (mediaItem) => !mediaItem.main && mediaItem.type === ProductMediaType.IMAGE
  )
  const mainImage = mainImages?.[0]
  // With no main image the first secondary stands in for it, so the hover image
  // has to skip ahead to the next one.
  const secondaryImage = mainImage ? secondaryImages?.[0] : secondaryImages?.[1]
  const mainImageUrl = httpImageFactory((mainImage ?? secondaryImages?.[0])?.url || '')
  const secondaryImageUrl = httpImageFactory(secondaryImage?.url || '')

  return {
    mainImages,
    secondaryImages,
    mainImageUrl,
    secondaryImageUrl
  }
}
